import * as React from "react"
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  useReactTable,
  PaginationState,
  OnChangeFn,
} from "@tanstack/react-table"

import { Skeleton } from "components/ui/skeleton"
import { DataTablePagination } from "./data-table-pagination"
import { DataTableToolbar } from "./data-table-toolbar"
import { ArrivalBucket, LoadType, TripType } from "types/shipment"

interface DateRange {
  from?: Date
  to?: Date
}
export interface AlertFilter {
  arrival?: ArrivalBucket;
  trip_type?: TripType;
  load_type?: LoadType;
  pod?: boolean;
  freight_invoice?: boolean;
  shipment_status?: string;
}

interface AlertBucket {
  label: string;
  count: number;
  filters: AlertFilter;
}

export interface AlertCard {
  id: string;
  title: string;
  type: 'bucketed' | 'single';
  style: 'positive' | 'negative' | 'neutral' | 'total';
  count?: number;
  filters?: AlertFilter;
  buckets?: AlertBucket[];
}

interface CustomerOption {
  cust_code: string;
  cust_name: string;
}

interface BranchOption {
  ou_code: string;
  branch_name: string;
}

export interface DivisionOption {
  divn_id: number;
  divn_name: string;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  loading?: boolean
  pagination?: PaginationState
  onPaginationChange?: OnChangeFn<PaginationState>
  sorting?: SortingState
  onSortingChange?: OnChangeFn<SortingState>
  globalFilter?: string
  onGlobalFilterChange?: (value: string) => void
  dateRange?: DateRange
  onDateRangeChange?: (dateRange: DateRange) => void
  totalCount?: number
  pageCount?: number
  alertCards?: AlertCard[];
  activeAlertFilters?: AlertFilter;
  onAlertFilterChange?: (filters: AlertFilter) => void;
  customerOptions?: CustomerOption[];
  selectedCustId?: string;
  onCustomerChange?: (code: string | undefined) => void;
  branchOptions?: BranchOption[];
  selectedOuCode?: string;
  onBranchChange?: (ouCode: string | undefined) => void;
  isLoadingAlerts?: boolean;
  alertSkeletonCount?: number;
  shipmentStatus?: string
  onShipmentStatusChange?: (value: string | undefined) => void
  hideToolbar?: boolean;
  currentTab?: string;
  divisionOptions?: DivisionOption[];
  selectedDivision?: number;
  onDivisionChange?: (division: number | undefined) => void;
  userType?: string;
  onResetFilters?: () => void;
  stockStatus?: string
  onStockStatusChange?: (value: string | undefined) => void
  stockLocation?: string
  stockLocationOptions?: BranchOption[]
  onStockLocationChange?: (value: string | undefined) => void
}

export function DataTable<TData, TValue>({
  columns,
  data,
  loading = false,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  globalFilter,
  onGlobalFilterChange,
  dateRange,
  onDateRangeChange,
  totalCount = 0,
  pageCount,
  alertCards = [],
  activeAlertFilters = {},
  onAlertFilterChange = () => { },
  customerOptions = [],
  selectedCustId,
  onCustomerChange = () => { },
  branchOptions = [],
  selectedOuCode,
  onBranchChange = () => { },
  isLoadingAlerts = false,
  alertSkeletonCount = 4,
  shipmentStatus,
  onShipmentStatusChange = () => { },
  hideToolbar = false,
  currentTab,
  divisionOptions = [],
  selectedDivision,
  onDivisionChange = () => {},
  userType,
  onResetFilters,
  stockStatus,
  onStockStatusChange = () => {},
  stockLocation,
  stockLocationOptions = [],
  onStockLocationChange = () => {},
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState({})
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting: sorting || [],
      columnVisibility,
      rowSelection,
      pagination: pagination || { pageIndex: 0, pageSize: 20 },
      globalFilter: globalFilter || '',
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: onSortingChange || (() => { }),
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: onPaginationChange || (() => { }),
    onGlobalFilterChange: onGlobalFilterChange || (() => { }),
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: pageCount || -1,
  })

  return (
    <div className="h-full flex flex-col">
      {!hideToolbar && (
        <div className="shrink-0 px-4">
          <DataTableToolbar
            table={table}
            dateRange={dateRange}
            onDateRangeChange={onDateRangeChange}
            alertCards={alertCards}
            activeAlertFilters={activeAlertFilters}
            onAlertFilterChange={onAlertFilterChange}
            customerOptions={customerOptions}
            selectedCustId={selectedCustId}
            onCustomerChange={onCustomerChange}
            branchOptions={branchOptions}
            selectedOuCode={selectedOuCode}
            onBranchChange={onBranchChange}
            isLoadingAlerts={isLoadingAlerts}
            shipmentStatus={shipmentStatus}
            onShipmentStatusChange={onShipmentStatusChange}
            currentTab={currentTab}
            divisionOptions={divisionOptions}
            selectedDivision={selectedDivision}
            onDivisionChange={onDivisionChange}
            userType={userType} 
            onResetFilters={onResetFilters}
            stockStatus={stockStatus}
            onStockStatusChange={onStockStatusChange}
            stockLocation={stockLocation}
            stockLocationOptions={stockLocationOptions}
            onStockLocationChange={onStockLocationChange}
          />
        </div>
      )}
      <div className="flex-1 min-h-0 mx-4 border border-gray-200 bg-background">
        <div className="h-full w-full overflow-auto">
          <table className="w-max min-w-full relative text-sm">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-gray-200 bg-slate-50/80 sticky top-0 z-10">
                  {headerGroup.headers.map((header) => {
                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        className="h-8 px-2 text-left align-middle font-normal text-xs text-gray-600 [&:has([role=checkbox])]:pr-0 bg-slate-50/80"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </th>
                    )
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="bg-background text-slate-700">
              {loading ? (
                // Skeleton rows for loading state
                Array.from({ length: Math.min(pagination?.pageSize || 20, 15) }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="border-b border-gray-100">
                    {columns.map((_, colIndex) => (
                      <td key={colIndex} className="h-9 px-2">
                        <Skeleton className="h-3 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className="border-b border-gray-100 transition-colors hover:bg-slate-50/40 data-[state=selected]:bg-blue-50/30"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="h-9 px-2 align-middle text-xs [&:has([role=checkbox])]:pr-0">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="h-16 text-center text-xs text-slate-500"
                  >
                    No results.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="shrink-0 p-4">
        <DataTablePagination table={table} totalCount={totalCount} />
      </div>
    </div>
  )
}
